"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { radicalSign } from "@/lib/tool-kit/arithmetic/radicals";
import {
  computeRoot,
  type RootComputation,
  type RootView,
  rootWorking,
} from "@/lib/tool-kit/arithmetic/root-view";
import { toSuperscript } from "@/lib/tool-kit/math/primes";

/** English strings. A translated content file overrides these through `ui`. */
export const SQUARE_ROOT_CALCULATOR_STRINGS = {
  numberLabel: "Number under the root (the radicand)",
  numberHint: "A whole number, decimal or fraction: 72, 0.5 or 2/3.",
  indexLabel: "Root index",
  indexHint: "2 for a square root, 3 for a cube root, up to 100.",
  placesLabel: "Decimal places",
  results: "Results",
  root: "Principal root",
  radical: "Simplest radical form",
  perfectSquare: "Perfect square?",
  perfectCube: "Perfect cube?",
  perfectIndex: "Perfect power with index {n}?",
  yes: "Yes",
  no: "No",
  exactly: "Yes, exactly {root}",
  imaginary: "Imaginary result",
  copy: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  factorLine: "{x} = {factors}",
  pullOutSquare:
    "Every pair of equal factors leaves the root as a single factor, so {outside} comes out and {inside} stays inside.",
  pullOutIndex:
    "Every group of {n} equal factors leaves the root as a single factor, so {outside} comes out and {inside} stays inside.",
  nothingOut:
    "No factor of {x} appears often enough to leave the root, so {sign}{x} is already in simplest form.",
  radicalLine: "{sign}{x} = {radical}",
  perfectLine:
    "{sign}{x} = {root}: {x} is a perfect power, so the root is exact.",
  boundsHeading: "Between two perfect powers",
  boundsLine:
    "{x} lies between {k}{sup} = {lower} and {k1}{sup} = {upper}, so {sign}{x} is between {k} and {k1}.",
  negativeOdd:
    "An odd root of a negative number is real and negative: the sign carries through, since (−a)ⁿ is negative when n is odd.",
  imaginaryLine:
    "A square root of a negative number is imaginary. The principal value is i times the root of the positive number: {sign}({x}) = i{sign}{abs}.",
  noRealRoot:
    "A negative number has no real root of an even index. The principal root is a complex number, which this calculator does not compute.",
  heronHeading: "Heron’s method",
  heronIntro:
    "Start from a guess, then replace it by the average of the guess and x divided by the guess. The number of correct digits roughly doubles each step.",
  columnStep: "Step",
  columnEstimate: "Estimate",
  columnSquare: "Estimate squared",
  enterNumber: "Enter a number and a root index to see the root.",
  invalidNumber: "The number is not valid. Try 72, 0.5 or 2/3.",
  invalidIndex: "The root index must be a whole number from 1 to 100.",
  tooBig:
    "That is too much to compute digit by digit. Try fewer digits or fewer places.",
  copyText: "{sign}{x} ≈ {value}",
  copyExact: "{sign}{x} = {value}",
} as const;

type Strings = typeof SQUARE_ROOT_CALCULATOR_STRINGS;

const PLACE_CHOICES = [2, 4, 6, 8, 10, 15, 20, 30, 50];
const TRAILING_ZEROS = /0+$/;

/** An exact decimal with its padding zeros removed: 4.0000 → 4. */
function trimExactDigits(text: string): string {
  if (!text.includes(".")) {
    return text;
  }
  const trimmed = text.replace(TRAILING_ZEROS, "");
  return trimmed.endsWith(".") ? trimmed.slice(0, -1) : trimmed;
}

function perfectLabel(view: RootView, strings: Strings): string {
  if (view.index === 2) {
    return strings.perfectSquare;
  }
  if (view.index === 3) {
    return strings.perfectCube;
  }
  return t(strings.perfectIndex, { n: view.index });
}

function rootDisplay(view: RootView): { text: string; exact: boolean } {
  const digits = view.digits;
  if (digits === null) {
    return { text: "", exact: false };
  }
  const sign = view.negative && view.kind === "real" ? "−" : "";
  const exact = digits.exact;
  const text = exact ? trimExactDigits(digits.text) : digits.text;
  return { text: `${sign}${text}`, exact };
}

function ResultStats({ view, strings }: { view: RootView; strings: Strings }) {
  const display = rootDisplay(view);
  const shown = view.kind === "imaginary" ? `${display.text} i` : display.text;
  const headline = display.exact ? shown : `≈ ${shown}`;
  const exactRoot = view.exactRoot;
  const exactText =
    exactRoot === null
      ? ""
      : `${exactRoot.num}${exactRoot.den === BigInt(1) ? "" : `/${exactRoot.den}`}`;
  return (
    <StatGrid>
      <StatCard
        emphasis
        label={view.kind === "imaginary" ? strings.imaginary : strings.root}
        value={headline}
      />
      {view.radicalText ? (
        <StatCard
          label={strings.radical}
          value={`${view.kind === "imaginary" ? "i " : ""}${view.radicalText}`}
        />
      ) : null}
      {view.kind === "real" ? (
        <StatCard
          label={perfectLabel(view, strings)}
          value={
            view.perfect
              ? t(strings.exactly, {
                  root: `${view.negative ? "−" : ""}${exactText}`,
                })
              : strings.no
          }
        />
      ) : null}
    </StatGrid>
  );
}

function Bounds({ view, strings }: { view: RootView; strings: Strings }) {
  if (view.bounds === null) {
    return null;
  }
  const { root, lower, upper } = view.bounds;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.boundsHeading}</h3>
      <p className="text-foreground text-sm">
        {t(strings.boundsLine, {
          x: view.xText.replace("−", ""),
          k: root.toString(),
          k1: (root + BigInt(1)).toString(),
          sup: toSuperscript(view.index),
          lower: lower.toString(),
          upper: upper.toString(),
          sign: radicalSign(view.index),
        })}
      </p>
    </>
  );
}

function Heron({ view, strings }: { view: RootView; strings: Strings }) {
  const fmt = useToolNumber();
  if (view.heron === null) {
    return null;
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.heronHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.heronIntro}</p>
      <DataTable
        head={[
          strings.columnStep,
          strings.columnEstimate,
          strings.columnSquare,
        ]}
        rows={view.heron.map((row) => [
          row.step,
          fmt(row.estimate, { maximumSignificantDigits: 12 }),
          fmt(row.square, { maximumSignificantDigits: 12 }),
        ])}
      />
    </>
  );
}

function Notes({ view, strings }: { view: RootView; strings: Strings }) {
  const sign = radicalSign(view.index);
  const abs = view.xText.replace("−", "");
  return (
    <>
      {view.negative && view.kind === "real" ? (
        <Notice>{strings.negativeOdd}</Notice>
      ) : null}
      {view.kind === "imaginary" ? (
        <Notice>
          {t(strings.imaginaryLine, { sign, x: view.xText, abs })}
        </Notice>
      ) : null}
      {view.kind === "none" ? (
        <ErrorNotice>{strings.noRealRoot}</ErrorNotice>
      ) : null}
    </>
  );
}

function Problem({
  kind,
  strings,
}: {
  kind: Exclude<RootComputation["kind"], "ok">;
  strings: Strings;
}) {
  switch (kind) {
    case "empty":
      return <Notice>{strings.enterNumber}</Notice>;
    case "invalid-number":
      return <ErrorNotice>{strings.invalidNumber}</ErrorNotice>;
    case "invalid-index":
      return <ErrorNotice>{strings.invalidIndex}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.tooBig}</ErrorNotice>;
  }
}

function Outcome({
  outcome,
  strings,
}: {
  outcome: RootComputation;
  strings: Strings;
}) {
  if (outcome.kind !== "ok") {
    return <Problem kind={outcome.kind} strings={strings} />;
  }
  const { view } = outcome;
  const working = rootWorking(view, strings);
  const display = rootDisplay(view);
  return (
    <>
      {view.kind === "none" ? null : (
        <ResultStats strings={strings} view={view} />
      )}
      <Notes strings={strings} view={view} />
      {view.digits === null ? null : (
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={t(display.exact ? strings.copyExact : strings.copyText, {
            sign: radicalSign(view.index),
            x: view.xText,
            value: `${display.text}${view.kind === "imaginary" ? " i" : ""}`,
          })}
        />
      )}
      {working.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
            {working.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}
      <Bounds strings={strings} view={view} />
      <Heron strings={strings} view={view} />
    </>
  );
}

export default function SquareRootCalculatorWidget() {
  const strings = useToolUi(SQUARE_ROOT_CALCULATOR_STRINGS);
  const [number, setNumber] = useState("72");
  const [index, setIndex] = useState("2");
  const [places, setPlaces] = useState("10");
  const outcome = useMemo(
    () => computeRoot(number, index, Number(places)),
    [number, index, places]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.numberHint}
          id="sr-number"
          label={strings.numberLabel}
          mono
          numeric
          onChange={setNumber}
          value={number}
        />
        <TextField
          hint={strings.indexHint}
          id="sr-index"
          label={strings.indexLabel}
          mono
          numeric
          onChange={setIndex}
          value={index}
        />
        <SelectField
          id="sr-places"
          label={strings.placesLabel}
          onChange={setPlaces}
          options={PLACE_CHOICES.map((n) => ({
            value: String(n),
            label: String(n),
          }))}
          value={places}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome outcome={outcome} strings={strings} />
      </section>
    </>
  );
}
